import { useCallback, useMemo, useReducer, useState } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { buildEstablishmentsList } from '../utils/build-establishments-list.util';
import { isPhysicalPersonDirector } from '../utils/is-physical-person-director.util';
import type { ArmedDirector } from '../DirectorsResultsList';
import type { ArmedEstablishment } from '../EstablishmentsResultsList';
import type { EstablishmentListItem } from '../utils/build-establishments-list.util';
import type { ActiveMembership } from '../types/active-membership.type';
import type {
  OrganizationSearchDirectorPersonnePhysique,
  OrganizationSearchResult,
} from '../types/organization-search-result.type';

type State =
  | Readonly<{ phase: 'idle' }>
  | Readonly<{ phase: 'directors-select'; result: OrganizationSearchResult }>
  | Readonly<{
      phase: 'directors-create';
      activeMemberships: readonly ActiveMembership[];
      armedDirectors: readonly ArmedDirector[];
      result: OrganizationSearchResult;
    }>
  | Readonly<{
      phase: 'establishments-select';
      activeMemberships: readonly ActiveMembership[];
      result: OrganizationSearchResult;
    }>
  | Readonly<{
      phase: 'establishments-create';
      activeMemberships: readonly ActiveMembership[];
      armedEstablishments: readonly ArmedEstablishment[];
      result: OrganizationSearchResult;
    }>;

type Action =
  | Readonly<{
      type: 'started';
      hasPhysicalPersonDirectors: boolean;
      result: OrganizationSearchResult;
    }>
  | Readonly<{
      type: 'directorsArmed';
      armedDirectors: readonly ArmedDirector[];
    }>
  | Readonly<{ type: 'directorsSkipped' }>
  | Readonly<{ type: 'continuedToEstablishments' }>
  | Readonly<{
      type: 'activeMembershipsChanged';
      activeMemberships: readonly ActiveMembership[];
    }>
  | Readonly<{
      type: 'establishmentsArmed';
      armedEstablishments: readonly ArmedEstablishment[];
    }>
  | Readonly<{ type: 'cancelled' }>;

const initialState: State = { phase: 'idle' };

const reducer = (state: Readonly<State>, action: Readonly<Action>): State => {
  switch (action.type) {
    case 'started':
      return action.hasPhysicalPersonDirectors
        ? { phase: 'directors-select', result: action.result }
        : {
            phase: 'establishments-select',
            activeMemberships: EMPTY_ARRAY,
            result: action.result,
          };

    case 'directorsArmed':
      return state.phase === 'directors-select'
        ? {
            phase: 'directors-create',
            activeMemberships: EMPTY_ARRAY,
            armedDirectors: action.armedDirectors,
            result: state.result,
          }
        : state;

    case 'directorsSkipped':
      return state.phase === 'directors-select'
        ? {
            phase: 'establishments-select',
            activeMemberships: EMPTY_ARRAY,
            result: state.result,
          }
        : state;

    case 'continuedToEstablishments':
      return state.phase === 'directors-create'
        ? {
            phase: 'establishments-select',
            activeMemberships: state.activeMemberships,
            result: state.result,
          }
        : state;

    case 'activeMembershipsChanged':
      return state.phase === 'directors-create'
        ? { ...state, activeMemberships: action.activeMemberships }
        : state;

    case 'establishmentsArmed':
      return state.phase === 'establishments-select'
        ? {
            phase: 'establishments-create',
            activeMemberships: state.activeMemberships,
            armedEstablishments: action.armedEstablishments,
            result: state.result,
          }
        : state;

    case 'cancelled':
      return initialState;

    default: {
      const unhandledAction: never = action;
      return unhandledAction;
    }
  }
};

export const useCreateOrganizationFromSearchResult = () => {
  const { civilitiesCodeMap, defaultCivility } = useReferencesContext();

  const civilityForUnknownGender = Object.values(civilitiesCodeMap).find(
    (civility) => civility.useWhenUnknown,
  );
  const fallbackCivilityId =
    civilityForUnknownGender?._id ??
    defaultCivility?._id ??
    civilitiesCodeMap.MadamOrMister._id;

  const [state, dispatch] = useReducer(reducer, initialState);
  const [hasPendingDirectorRows, setHasPendingDirectorRows] = useState(false);
  const [hasPendingEstablishmentRows, setHasPendingEstablishmentRows] =
    useState(false);

  const pendingResult = state.phase === 'idle' ? null : state.result;

  const physicalPersonDirectors = useMemo(
    () =>
      (pendingResult?.dirigeants ?? EMPTY_ARRAY).filter(
        isPhysicalPersonDirector,
      ),
    [pendingResult],
  );

  const establishmentsForDialog = useMemo(
    () =>
      pendingResult ? buildEstablishmentsList(pendingResult) : EMPTY_ARRAY,
    [pendingResult],
  );

  const isPreparingOrganization =
    hasPendingDirectorRows || hasPendingEstablishmentRows;

  const startCreateOrganization = useCallback(
    (result: OrganizationSearchResult) => {
      const hasPhysicalPersonDirectors = (
        result.dirigeants ?? EMPTY_ARRAY
      ).some(isPhysicalPersonDirector);

      dispatch({ type: 'started', hasPhysicalPersonDirectors, result });
    },
    [],
  );

  const cancelPendingOrganization = useCallback(() => {
    dispatch({ type: 'cancelled' });
    setHasPendingDirectorRows(false);
    setHasPendingEstablishmentRows(false);
  }, []);

  const startDirectorsCreation = useCallback(
    (
      selectedDirectors: readonly OrganizationSearchDirectorPersonnePhysique[],
    ) => {
      if (selectedDirectors.length === 0) {
        dispatch({ type: 'directorsSkipped' });
        return;
      }

      const selectedSet = new Set(selectedDirectors);
      dispatch({
        type: 'directorsArmed',
        armedDirectors: physicalPersonDirectors.map((director) => ({
          director,
          isSelected: selectedSet.has(director),
        })),
      });
    },
    [physicalPersonDirectors],
  );

  const continueToEstablishments = useCallback(() => {
    dispatch({ type: 'continuedToEstablishments' });
  }, []);

  const setActiveMemberships = useCallback(
    (activeMemberships: readonly ActiveMembership[]) => {
      dispatch({ type: 'activeMembershipsChanged', activeMemberships });
    },
    [],
  );

  const startEstablishmentsCreation = useCallback(
    (selectedEstablishments: readonly EstablishmentListItem[]) => {
      const selectedSet = new Set(selectedEstablishments);
      dispatch({
        type: 'establishmentsArmed',
        armedEstablishments: establishmentsForDialog.map((item) => ({
          isSelected: selectedSet.has(item),
          item,
        })),
      });
    },
    [establishmentsForDialog],
  );

  return {
    activeMemberships:
      state.phase === 'directors-create' ||
      state.phase === 'establishments-select' ||
      state.phase === 'establishments-create'
        ? state.activeMemberships
        : EMPTY_ARRAY,
    armedDirectors:
      state.phase === 'directors-create' ? state.armedDirectors : null,
    armedEstablishments:
      state.phase === 'establishments-create'
        ? state.armedEstablishments
        : null,
    cancelPendingOrganization,
    continueToEstablishments,
    establishmentsForDialog,
    fallbackCivilityId,
    hasPendingDirectorRows,
    hasPendingEstablishmentRows,
    isDirectorsDialogOpen:
      state.phase === 'directors-select' || state.phase === 'directors-create',
    isEstablishmentsDialogOpen:
      state.phase === 'establishments-select' ||
      state.phase === 'establishments-create',
    isPreparingOrganization,
    pendingResult,
    physicalPersonDirectors,
    setActiveMemberships,
    setHasPendingDirectorRows,
    setHasPendingEstablishmentRows,
    startCreateOrganization,
    startDirectorsCreation,
    startEstablishmentsCreation,
  };
};
